import NewsArticle from "@/components/NewsArticle";
import SEO from "@/components/SEO";
import {
  filterDataToSingleItem,
  usePreviewSubscription,
} from "@/lib/sanity/client";
import {
  mainLayoutQuery,
  MainLayoutQuery,
  settingsQuery,
  seoQuerySchema,
} from "@/lib/sanity/queries";
import { getClient } from "@/lib/sanity/server";
import type { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { groq } from "next-sanity";
import { useEffect } from "react";
import { z } from "zod";

type UrlQuery = {
  slug: string;
};

export const getStaticPaths: GetStaticPaths<UrlQuery> = async () => {
  // Pre-render only the first 10 articles to avoid slowing down builds
  const query = groq`*[_type == "article" && defined(slug.current)][0..10].slug.current`;
  const slugs = await getClient().fetch<string[]>(query);
  return {
    paths: slugs.map((slug) => "/news/" + slug),
    fallback: "blocking",
  };
};

const pageDataSchema = z.object({
  _id: z.string(), // required for preview
  heading: z.string(),
  seo: seoQuerySchema,
  body: z.array(z.object({}).passthrough()).nullable(),
  coverImage: z.object({ asset: z.object({ url: z.string() }) }).nullable(),
  releaseDate: z.string(),
  category: z.string().nullable(),
  allArticles: z.array(
    z.object({
      _id: z.string(),
      slug: z.string().nullable(),
    })
  ),
});

type PageData = z.infer<typeof pageDataSchema>;

type Props = {
  preview: boolean;
  data: {
    page: PageData;
    query: string;
    queryParams: {
      slug: string;
      locale?: string;
      defaultLocale?: string;
    };
  };
  settings: any;
  layout: MainLayoutQuery;
};

export const getStaticProps: GetStaticProps<Props, UrlQuery> = async (ctx) => {
  const { locale = "it", defaultLocale = "it", params, preview = false } = ctx;

  const slug = params?.slug;
  if (!slug) return { notFound: true };

  const queryParams = { slug, locale, defaultLocale };
  const query = groq`*[_type == "article" && slug.current == $slug] {
    _id,
    seo {
      "metaTitle": coalesce(metaTitle[$locale], metaTitle[$defaultLocale]),
      "metaDesc": coalesce(metaDesc[$locale], metaDesc[$defaultLocale]),
      "shareTitle": coalesce(shareTitle[$locale], shareTitle[$defaultLocale]),
      "shareDesc": coalesce(shareDesc[$locale], shareDesc[$defaultLocale]),
      shareGraphic {
        asset->{
          ...,
          "alt": coalesce(alt[$locale], alt[$defaultLocale])
        }
      }
    },
    "heading": coalesce(heading[$locale], heading[$defaultLocale]),
    "category": category->{"title": coalesce(title[$locale], title[$defaultLocale])}.title,
    "body": coalesce(body[$locale], body[$defaultLocale])[] {
      ...,
      children[] {
        ...,
        _type == "richTextImage" => {
          image {
            asset->{
              url,
            }
          }
        },
      },
      markDefs[] {
        ...,
        _type == "internalLink" => {
          "slug": @.reference->slug.current,
          params,
        },
        _type == "externalLink" => {
          url,
          blank
        }
      }
    },
    releaseDate,
    coverImage {
      asset->{ url }
    },
    "allArticles": *[_type == "article"] | order(orderRank desc) {
      _id,
      "slug": slug.current,
    },
  }`;
  const res = await getClient(preview).fetch(query, queryParams);
  const data = z.array(pageDataSchema).parse(res);

  // Escape hatch, if our query failed to return data
  if (!data?.length) return { notFound: true };

  // Helper function to reduce all returned documents down to just one
  const page = filterDataToSingleItem<PageData>(data, preview);

  const [settings, layout] = await Promise.all([
    // Get site settings
    getClient(false).fetch(settingsQuery, {
      locale,
      defaultLocale,
    }),
    // Get Layout data
    getClient(false).fetch(mainLayoutQuery, {
      locale,
      defaultLocale,
    }),
  ]);

  return {
    props: {
      preview,
      settings,
      layout,
      data: {
        page,
        query,
        queryParams,
      },
    },
    revalidate: 60,
  };
};

const Page: NextPage<Props> = ({ data, preview }) => {
  const { data: previewData, error } = usePreviewSubscription(data.query, {
    params: data.queryParams,
    // The hook will return this on first render
    // This is why it's important to fetch *draft* content server-side!
    initialData: data.page,
    // The passed-down preview context determines whether this function does anything
    enabled: preview,
  });

  useEffect(() => {
    if (error) console.error(error);
  }, [error]);

  // Client-side uses the same query, so we may need to filter it down again
  const page = filterDataToSingleItem<PageData>(previewData, preview);
  const prevSlug = page.allArticles.filter((article, index) => {
    if (page._id === page.allArticles[index + 1]?._id) return true;
  });
  const nextSlug = page.allArticles.filter((article, index) => {
    if (page._id === page.allArticles[index - 1]?._id) return true;
  });

  if (page.coverImage && page.body) {
    return (
      <>
        {page.seo && (
          <SEO {...page.seo} slug={`news/${data?.queryParams?.slug}`} />
        )}
        <NewsArticle
          heading={page.heading}
          coverImage={page.coverImage}
          category={page.category}
          releaseDate={page.releaseDate}
          body={page.body}
          prevSlug={prevSlug.length > 0 ? prevSlug[0].slug : null}
          nextSlug={nextSlug.length > 0 ? nextSlug[0].slug : null}
        />
      </>
    );
  } else {
    return null;
  }
};

export default Page;
